// HFLedger/TransactionForm.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TransactionForm.css';

function TransactionForm({ user, language, SERVER_ROOT, settings, accounts, categories, onSubmit, transaction, onAccountNeeded, onCategoryCreated }) {
  
  const [isEditing, setIsEditing] = useState(!!transaction);
  const [formData, setFormData] = useState({
    user_id: user?.uid || '',
    account_id: '',
    category_id: '',
    amount: '',
    formattedAmount: '', // 포맷팅된 금액 추가
    type: 'expense', // 기본값은 지출
    currency: settings.default_currency,
    description: '',
    transaction_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    location: '',
    receiptImage: null // 영수증 이미지 필드 추가
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState([]); 

  // 카테고리 기본 색상 (여기에 추가)
  const defaultColors = [
    '#4CAF50', '#F44336', '#2196F3', '#FF9800', '#9C27B0', 
    '#009688', '#673AB7', '#FFEB3B', '#795548', '#607D8B'
  ];

  const [newCategoryMode, setNewCategoryMode] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
 
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent); 

  // 트랜잭션 데이터 설정
  useEffect(() => {
    if (transaction) {
      setFormData({
        user_id: transaction.user_id,
        account_id: transaction.account_id,
        category_id: transaction.category_id,
        amount: transaction.amount,
        type: transaction.type,
        currency: transaction.currency,
        description: transaction.description || '',
        transaction_date: transaction.transaction_date.split('T')[0],
        payment_method: transaction.payment_method || '',
        location: transaction.location || ''
      });
    }
  }, [transaction]);

  // 컴포넌트 마운트 시 계좌 정보 로깅
  useEffect(() => {
    console.log("계좌 목록 로드:", accounts);
    
    // 계좌 목록 상세 검사
    console.log("=== 계좌 목록 상세 검사 ===");
    console.log("계좌 개수:", accounts?.length || 0);
    
    if (accounts && accounts.length > 0) {
      accounts.forEach((account, index) => {
        console.log(`계좌 ${index + 1}:`, {
          account_id: account.account_id,
          account_name: account.account_name,
          account_type: account.account_type,
          current_balance: account.current_balance
        });
        
        // 필수 필드 검사
        if (!account.account_id) {
          console.warn(`경고: 계좌 ${index + 1}에 account_id가 없음`);
        }
        if (!account.account_type && !account.account_name) {
          console.warn(`경고: 계좌 ${index + 1}에 account_type와 account_name이 모두 없음`);
        }
      });
      
      // 계좌가 있을 경우 첫 번째 계좌를 기본 선택
      if (!formData.account_id) {
        console.log("기본 계좌 설정:", accounts[0]);
        setFormData(prev => ({
          ...prev,
          account_id: accounts[0].account_id,
          payment_method: accounts[0].account_type || accounts[0].account_name
        }));
      }
    } else {
      console.warn("계좌 목록이 비어 있음");
    }
  }, [accounts]);

  // 트랜잭션 타입에 따른 카테고리 필터링 
  useEffect(() => {
    console.log("카테고리 목록:", categories);
    console.log("현재 폼 데이터:", formData);
    
    if (categories && categories.length > 0) {
      const filtered = categories.filter(category => category.type === formData.type);
      setFilteredCategories(filtered);
      
      // 선택된 카테고리가 필터링된 목록에 없으면 '기타 지출' 카테고리 찾기
      if (filtered.length > 0) {
        // 선택된 카테고리가 없거나 필터링된 목록에 없는 경우
        if (!formData.category_id || !filtered.some(c => c.category_id === formData.category_id)) {
          // '기타 지출' 카테고리 찾기
          const otherExpenseCategory = filtered.find(c => c.name === '기타 지출');
          
          // '기타 지출' 카테고리가 있으면 선택, 없으면 첫 번째 카테고리 선택
          setFormData(prev => ({
            ...prev,
            category_id: otherExpenseCategory ? otherExpenseCategory.category_id : filtered[0].category_id
          }));
        }
      }
    }
  }, [categories, formData.type, formData.category_id]);

  // 새 카테고리 생성 함수
  const createNewCategory = async () => {
    if (!newCategoryName.trim()) {
      setError(language === 'KR' 
        ? '카테고리 이름을 입력해주세요.' 
        : 'Please enter a category name.');
      return null;
    }
    
    try {
      // 기본 색상 랜덤 선택
      const randomColor = defaultColors[Math.floor(Math.random() * defaultColors.length)];
      
      // 새 카테고리 생성
      const response = await axios.post(`${SERVER_ROOT}/api/hfl/categories`, {
        user_id: user.uid,
        name: newCategoryName.trim(),
        type: formData.type,
        color: randomColor,
        icon: ''
      });
      
      const newCategory = response.data;
      
      // 카테고리 목록 업데이트 (현재 화면용)
      setFilteredCategories(prev => [...prev, newCategory]);
      
      // 전체 카테고리 목록 업데이트 (상위 컴포넌트에 알림)
      if (typeof onCategoryCreated === 'function') {
        onCategoryCreated(newCategory);
      }
      
      // formData에 새 카테고리 ID 설정
      setFormData(prev => ({
        ...prev,
        category_id: newCategory.category_id
      }));
      
      // 신규 생성 모드 종료
      setNewCategoryMode(false);
      setNewCategoryName('');
      
      return newCategory.category_id;
    } catch (err) {
      console.error('Error creating new category:', err);
      setError(err.message || (language === 'KR' 
        ? '새 카테고리를 생성하는 중 오류가 발생했습니다.' 
        : 'Error creating new category.'));
      return null;
    }
  };

  // 입력 변경 핸들러 
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    console.log(`입력 변경: ${name} = ${value}`);
    
    if (name === 'amount' && formData.currency === 'KRW') {
      // 쉼표 제거 후 숫자만 추출
      const numericValue = value.replace(/,/g, '');
      
      // 숫자만 입력되도록 검증
      if (numericValue === '' || /^\d+$/.test(numericValue)) {
        // 천 단위 쉼표 형식으로 포맷팅
        const formatted = numericValue === '' ? '' : 
          Number(numericValue).toLocaleString('ko-KR');
        
        setFormData(prev => ({
          ...prev,
          amount: numericValue, // 원래 숫자값 저장
          formattedAmount: formatted // 포맷팅된 값 저장
        }));
      }
    } else if (name === 'account_id') {
      // 계좌가 변경되면 해당 계좌의 정보로 payment_method 업데이트
      const selectedAccount = accounts.find(account => account.account_id === value);
      console.log("선택된 계좌:", selectedAccount);
      
      if (selectedAccount) {
        const paymentMethod = selectedAccount.account_type || selectedAccount.account_name;
        console.log("설정된 payment_method:", paymentMethod);
        
        setFormData(prev => ({
          ...prev,
          account_id: value,
          payment_method: paymentMethod
        }));
      } else {
        console.log("계좌를 찾을 수 없음");
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 영수증 이미지 업로드 핸들러 

  const [receiptPreview, setReceiptPreview] = useState(null); // 상태 추가

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 이미지 리사이즈를 위한 함수
      const resizeImage = (file, maxWidth, maxHeight, callback) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            // 캔버스에 이미지를 그려서 리사이즈
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // 비율 유지하면서 크기 조정
            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width *= maxHeight / height;
                height = maxHeight;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // 리사이즈된 이미지를 Blob으로 변환
            canvas.toBlob((blob) => {
              // File 객체로 변환
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              callback(resizedFile);
            }, 'image/jpeg', 0.7); // 품질 70%로 설정
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      };
      
      // 이미지 리사이즈 호출
      resizeImage(file, 200, 200, (resizedFile) => {
        // 미리보기 URL 생성
        const previewURL = URL.createObjectURL(resizedFile);
        
        setFormData(prev => ({
          ...prev,
          receiptImage: resizedFile
        }));
        
        // 미리보기 URL 저장
        setReceiptPreview(previewURL);
      });
    }
  };

  // 영수증 제거 핸들러 추가
  const handleRemoveReceipt = () => {
    setFormData(prev => ({
      ...prev,
      receiptImage: null
    }));
    setReceiptPreview(null);
  };

  // 카메라 캡처 핸들러
  const handleCameraCapture = async () => {
    try {
      // 카메라 접근 및 이미지 캡처 로직
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // 여기서 실제로 카메라 캡처를 구현하거나 모달을 표시합니다
      // 구현 예시입니다 - 실제 기능은 별도 컴포넌트가 필요할 수 있습니다
      console.log("카메라 캡처 기능 호출됨");
    } catch (err) {
      console.error("카메라 접근 오류:", err);
      alert(language === 'KR' 
        ? '카메라에 접근할 수 없습니다. 권한을 확인해주세요.' 
        : 'Cannot access camera. Please check permissions.');
    }
  };

  // TransactionForm.js 약 line 30-40 근처에 추가
  // 계좌가 없을 때 메시지 표시
  if (accounts && accounts.length === 0) {
    return (
      <div className="transaction-form-container">
        <h2>{language === 'KR' ? '새 거래 추가' : 'Add New Transaction'}</h2>
        <div className="no-accounts-message">
          <i className="fas fa-exclamation-circle"></i>
          <p>
            {language === 'KR' 
              ? '거래를 추가하려면 먼저 계좌를 등록해야 합니다.' 
              : 'You need to add an account first to record transactions.'}
          </p>
          <button 
            className="add-account-button"
            onClick={() => {
              // 설정 탭으로 이동하는 함수 필요
              if (typeof onAccountNeeded === 'function') {
                onAccountNeeded();
              }
            }}
          >
            {language === 'KR' ? '계좌 등록하기' : 'Add Account'}
          </button>
        </div>
      </div>
    );
  }

  // 트랜잭션 타입 변경 핸들러
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      type: newType
    }));
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 필수 필드 검증 - 날짜, 계좌, 금액만 필수
    const requiredFields = ['transaction_date', 'account_id', 'amount'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    // 디버깅: 폼 데이터 로깅
    console.log("폼 데이터:", formData);
    console.log("필수 필드 확인:", {
      transaction_date: !!formData.transaction_date,
      account_id: !!formData.account_id,
      amount: !!formData.amount
    });
    console.log("계좌 목록:", accounts);
    
    if (missingFields.length > 0) {
      console.log("누락된 필드:", missingFields);
      setError(language === 'KR' 
        ? '날짜, 계좌, 금액은 필수 입력 항목입니다.' 
        : 'Date, account, and amount are required.');
      return;
    }
    
    // 금액이 비어있거나 0이하인지 확인
    const numericAmount = formData.amount.replace ? formData.amount.replace(/,/g, '') : formData.amount;
    if (isNaN(parseFloat(numericAmount)) || parseFloat(numericAmount) <= 0) {
      console.log("유효하지 않은 금액:", numericAmount);
      setError(language === 'KR' 
        ? '유효한 금액을 입력해주세요.' 
        : 'Please enter a valid amount.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // 신규 카테고리 생성 모드인 경우 먼저 카테고리 생성
      let categoryId = formData.category_id;
      
      if (newCategoryMode && newCategoryName.trim()) {
        categoryId = await createNewCategory();
        if (!categoryId) {
          setLoading(false);
          return; // 카테고리 생성 실패 시 중단
        }
      }
     
      // 폼 제출 핸들러 내의 새 트랜잭션 생성 부분 수정
      if (isEditing) {
        // 트랜잭션 업데이트 - 기존 코드 유지
        await axios.put(`${SERVER_ROOT}/api/hfl/transactions/${transaction.transaction_id}`, {
          category_id: formData.category_id,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          description: formData.description,
          transaction_date: formData.transaction_date,
          payment_method: formData.payment_method,
          location: formData.location
        });
      } else {
        // 새 트랜잭션 생성 - JSON 형식으로 변경
        const numericAmount = formData.amount.replace ? formData.amount.replace(/,/g, '') : formData.amount;

        // 기본값 설정
        let defaultCategory = null;
        if (!formData.category_id && filteredCategories.length > 0) {
          // 기타 지출 카테고리 찾기
          const otherExpenseCategory = filteredCategories.find(c => c.name === '기타 지출');
          defaultCategory = otherExpenseCategory ? otherExpenseCategory.category_id : filteredCategories[0].category_id;
        }

        // 선택된 계좌 확인
        const selectedAccount = accounts.find(acc => acc.account_id === formData.account_id);
        console.log("선택된 계좌:", selectedAccount);

        // JSON 객체 생성
        const transactionData = {
          user_id: formData.user_id,
          account_id: formData.account_id,
          category_id: formData.category_id || defaultCategory,
          amount: parseFloat(numericAmount),
          type: formData.type,
          currency: formData.currency,
          description: formData.description || (language === 'KR' ? '미지정 항목' : 'Unspecified item'),
          transaction_date: formData.transaction_date,
          payment_method: formData.payment_method || (selectedAccount ? selectedAccount.account_type || selectedAccount.account_name : ''),
          location: formData.location || ''
        };

        // 디버깅: 서버로 보내는 데이터 확인
        console.log("서버로 보내는 데이터:", transactionData);
        
        try {
          // 1. 먼저 트랜잭션 생성
          const response = await axios.post(`${SERVER_ROOT}/api/hfl/transactions`, transactionData);
          console.log("트랜잭션 생성 응답:", response.data);
          
          // 2. 영수증이 있는 경우 영수증 업로드
          if (formData.receiptImage) {
            const receiptFormData = new FormData();
            receiptFormData.append('receipt', formData.receiptImage);
            receiptFormData.append('user_id', formData.user_id);
            receiptFormData.append('transaction_id', response.data.transaction_id);
            
            try {
              const receiptResponse = await axios.post(
                `${SERVER_ROOT}/api/hfl/receipts/upload`, 
                receiptFormData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                }
              );
              console.log("영수증 업로드 응답:", receiptResponse.data);
            } catch (receiptError) {
              console.error("영수증 업로드 오류:", receiptError);
              // 영수증 업로드 실패해도 트랜잭션 저장은 완료된 상태로 처리
            }
          }
        } catch (error) {
          console.error("서버 오류:", error);
          console.log("오류 상세 정보:", error.response ? error.response.data : "응답 없음");
          
          // 응답 내용을 상세히 로깅
          if (error.response) {
            console.log("서버 응답 상태:", error.response.status);
            console.log("서버 오류 데이터:", error.response.data);
          }
          
          throw error;
        }
      }
      
      // 성공적으로 처리 완료
      if (onSubmit) onSubmit();
      
    } catch (err) {
      console.error('Error saving transaction:', err);
      setError(err.response?.data?.error || err.message || (language === 'KR' 
        ? '거래를 저장하는 중 오류가 발생했습니다.' 
        : 'Error saving transaction.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="transaction-form-container">
      {/* 수입/지출 선택 버튼 */}
      <div className="transaction-type-selector">
        <button 
          type="button"
          className={`type-button ${formData.type === 'income' ? 'income active' : 'income'}`}
          style={isMobileDevice ? {width: '60%'} : {width: '100%'}} 
          onClick={() => {
            setFormData(prev => ({ ...prev, type: 'income' }));
            setNewCategoryMode(false); // 타입 변경 시 신규 카테고리 모드 종료
          }}
        >
          {language === 'KR' ? '수입' : 'Income'}
        </button>
        <button 
          type="button"
          className={`type-button ${formData.type === 'expense' ? 'expense active' : 'expense'}`}
          style={isMobileDevice ? {width: '60%'} : {width: '100%'}} 
          onClick={() => {
            setFormData(prev => ({ ...prev, type: 'expense' }));
            setNewCategoryMode(false); // 타입 변경 시 신규 카테고리 모드 종료
          }}
        >
          {language === 'KR' ? '지출' : 'Expense'}
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="transaction-form">
        <div className="form-grid">
          {/* 왼쪽 컬럼 */}
          <div className="form-column left-column">
            {/* 날짜/시간 */}
            <div className="form-field" >
              <label htmlFor="transaction_date">
                {language === 'KR' ? '날짜/시간' : 'Date/Time'} <span className="required">*</span>
              </label>
              <input 
                type="date" 
                id="transaction_date"
                name="transaction_date"
                value={formData.transaction_date}
                onChange={handleInputChange} 
                required
              />
            </div>
            
            {/* 계좌 선택 */}
            <div className="form-field">
              <label htmlFor="account_id">
                {language === 'KR' ? '계좌' : 'Account'} <span className="required">*</span>
              </label>
              <select 
                id="account_id"
                name="account_id"
                value={formData.account_id} 
                onChange={(e) => {
                  const selectedAccountId = e.target.value;
                  const selectedAccount = accounts.find(account => account.account_id === selectedAccountId);
                  
                  console.log("선택된 계좌:", selectedAccount);
                  
                  if (selectedAccount) {
                    // 계좌 유형이 없으면 계좌 이름을 사용
                    const paymentMethod = selectedAccount.account_type || selectedAccount.account_name;
                    
                    setFormData(prev => ({
                      ...prev,
                      account_id: selectedAccountId,
                      payment_method: paymentMethod
                    }));
                    
                    console.log("계좌 선택 후 업데이트된 폼 데이터:", {
                      account_id: selectedAccountId,
                      payment_method: paymentMethod
                    });
                  }
                }}
                required
              >
                <option value="">{language === 'KR' ? '계좌를 선택하세요' : 'Select Account'}</option>
                {accounts.map(account => (
                  <option key={account.account_id} value={account.account_id}>
                    {account.account_name} ({account.account_type || 'Unknown'})
                  </option>
                ))}
              </select>
            </div>
            
            {/* payment_method를 hidden input으로 설정 */}
            <input 
              type="hidden" 
              name="payment_method"
              value={formData.payment_method}
            />
            
            {/* 금액 */}
            <div className="form-field">
              <label htmlFor="amount">
                {language === 'KR' ? '금액' : 'Amount'} <span className="required">*</span>
              </label>
              <div className="amount-input-container">
                <input 
                  type="text" 
                  id="amount"
                  name="amount"
                  value={formData.currency === 'KRW' ? formData.formattedAmount : formData.amount}
                  onChange={handleInputChange}
                  
                  placeholder="0"
                  required
                />
                {formData.currency === 'KRW' && 
                  <span className="currency-unit">원</span>
                }
              </div>
            </div>
          </div>
          
          {/* 오른쪽 컬럼 */}
          <div className="form-column right-column">
            {/* 영수증 입력 버튼 */}
            <div className="form-field">
              <label htmlFor="receipt-upload">
                {language === 'KR' ? '영수증' : 'Receipt'}
              </label>
              
              {receiptPreview ? (
                <div className="receipt-preview-container">
                  <img 
                    src={receiptPreview} 
                    alt="Receipt preview" 
                    className="receipt-preview-image" 
                  />
                  <button 
                    type="button"
                    className="remove-receipt-button"
                    onClick={handleRemoveReceipt}
                    title={language === 'KR' ? '영수증 제거' : 'Remove Receipt'}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <button 
                  type="button"
                  className="receipt-upload-button"
                  onClick={() => document.getElementById('receipt-upload').click()}
                >
                  <i className="fas fa-upload"></i>
                  {isMobileDevice 
                    ? <span>{language === 'KR' ? '사진 첨부' : 'Upload'}</span>
                    : <span>{language === 'KR' ? '영수증 첨부' : 'Upload Receipt'}</span>
                  }
                </button>
              )}
              
              <input 
                type="file"
                id="receipt-upload"
                accept="image/*"
                onChange={handleReceiptUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>
        
        {/* 카테고리 */} 
        <div className="form-field full-width" >
          <label htmlFor="category_id">
            {language === 'KR' ? '카테고리' : 'Category'}
          </label>
          
          {!newCategoryMode ? (
            <>
              <select 
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={(e) => {
                  if (e.target.value === 'new') {
                    setNewCategoryMode(true);
                  } else {
                    handleInputChange(e);
                  }
                }}
              >
                <option value="">{language === 'KR' ? '선택하세요' : 'Select Category'}</option>
                {filteredCategories.map(category => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.name}
                  </option>
                ))}
                <option value="new" style={{ fontWeight: 'bold' }}>
                  {language === 'KR' ? '+ 새 카테고리 추가' : '+ Add New Category'}
                </option>
              </select>
            </>
          ) : (
            <div className="new-category-input">
              <input 
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={language === 'KR' ? '새 이름을 쓰고, "카테고리 추가"버튼을 누르세요.' : 'Input a new category name and click add button'}
                autoFocus
              />
              <div className="new-category-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setNewCategoryMode(false);
                    setNewCategoryName('');
                  }}
                >
                  {language === 'KR' ? '취소' : 'Cancel'}
                </button>
                <button 
                  type="button" 
                  className="submit-button"
                  onClick={async () => {
                    const newCategoryId = await createNewCategory();
                    if (newCategoryId) {
                      // 카테고리 생성 성공 시 해당 카테고리 선택
                      setFormData(prev => ({
                        ...prev,
                        category_id: newCategoryId
                      }));
                    }
                  }}
                >
                  {language === 'KR' ? '카테고리 추가' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* 사용내역 */}
        <div className="form-field full-width">
          <label htmlFor="description">
            {language === 'KR' ? '사용내역' : 'Description'}
          </label>
          <input 
            type="text" 
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange} 
            placeholder={language === 'KR' ? 'OO에서 OO 구입' : 'Purchase OO at OO'}
          />
        </div>
        
        {/* 에러 메시지 */}
        {error && (
          <div className="form-error">
            {error}
          </div>
        )}
        
        {/* 제출 버튼 */}
        <div className="form-actions" style={{display: 'flex', justifyContent: 'center'}}>
          <button 
            type="submit" 
            className="submit-button" 
            disabled={loading}
          >
            {loading ? (
              <span className="loading-spinner"></span>
            ) : (
              isEditing 
                ? (language === 'KR' ? '수정 완료' : 'Update') 
                : (language === 'KR' ? '저장' : 'Save')
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TransactionForm;